import moment from "moment-timezone";

import { isMinuteOfDay } from "../shifts/shiftHelpers";

export const DEFAULT_BLACKOUT_PERIOD = {
  name: "",
  reason: "",
  startsAt: null,
  endsAt: null,
  startTime: 540,
  endTime: 1020,
  isActive: true,
};

export function localDate(instant, timezone) {
  if (!instant) return "";
  const value = moment(instant);
  if (!value.isValid()) return "";
  return timezone ? value.tz(timezone).format("YYYY-MM-DD") : value.format("YYYY-MM-DD");
}

/** Start of the selected calendar day in the hotel's timezone, serialized as UTC. */
export function dateBound(date, timezone) {
  if (!date) return null;
  const value = timezone ? moment.tz(date, timezone) : moment(date);
  return value.startOf("day").toISOString();
}

/** Start of the selected end calendar day in the hotel's timezone, in UTC. */
export function endDateBound(_startDate, endDate, timezone) {
  if (!endDate) return null;
  const value = timezone ? moment.tz(endDate, timezone) : moment(endDate);
  return value.startOf("day").toISOString();
}

export function validateBlackout(draft) {
  const errors = {};
  if (!draft.name?.trim()) errors.name = "Name is required";
  if (!isMinuteOfDay(draft.startTime)) errors.startTime = "Pick a start time";
  if (!isMinuteOfDay(draft.endTime)) errors.endTime = "Pick an end time";
  if (!errors.startTime && !errors.endTime && draft.startTime === draft.endTime) {
    errors.endTime = "End time must differ from the start time";
  }
  if (Boolean(draft.startDate) !== Boolean(draft.endDate)) {
    errors.endDate = "Choose both dates or leave both blank";
  } else if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) {
    errors.endDate = "End date must be on or after the start date";
  }
  return errors;
}

function minuteInWindow(period, minute) {
  return period.endTime > period.startTime
    ? minute >= period.startTime && minute < period.endTime
    : minute >= period.startTime || minute < period.endTime;
}

export function isBlackoutAt(period, day, minute, timezone) {
  if (!period?.isActive || !minuteInWindow(period, minute)) return false;
  const hour = Math.floor(minute / 60);
  const mins = minute % 60;
  const at = timezone
    ? moment.tz(`${day} ${hour}:${mins}`, "YYYY-MM-DD H:m", timezone)
    : moment(`${day} ${hour}:${mins}`, "YYYY-MM-DD H:m");
  if (!at.isValid()) return false;
  if (period.startsAt && at.isBefore(moment(period.startsAt))) return false;
  if (period.endsAt) {
    let end = moment(period.endsAt);
    /* Equal date bounds are the API representation of one hotel-local day. */
    if (
      period.startsAt &&
      moment(period.startsAt).valueOf() === end.valueOf()
    ) {
      end = timezone
        ? end.tz(timezone).add(1, "day").startOf("day")
        : end.add(1, "day").startOf("day");
    }
    if (!at.isBefore(end)) return false;
  }
  return true;
}

/** Whether an absolute pickup instant falls inside this hotel's blackout. */
export function isBlackoutInstant(period, instant, timezone) {
  if (!instant) return false;
  const local = timezone ? moment(instant).tz(timezone) : moment(instant);
  if (!local.isValid()) return false;

  return isBlackoutAt(
    period,
    local.format("YYYY-MM-DD"),
    local.hour() * 60 + local.minute(),
    timezone,
  );
}

export function blackoutCoverage(periods, day, startMinute, duration, timezone) {
  const start = timezone
    ? moment.tz(`${day} 00:00`, "YYYY-MM-DD HH:mm", timezone)
    : moment(`${day} 00:00`, "YYYY-MM-DD HH:mm");
  if (!start.isValid()) return "NONE";

  let covered = 0;
  for (let minute = 0; minute < duration; minute += 1) {
    const instant = start.clone().add(startMinute + minute, "minutes").toISOString();
    if (periods.some((period) => isBlackoutInstant(period, instant, timezone))) {
      covered += 1;
    }
  }

  if (covered === 0) return "NONE";
  return covered === duration ? "FULL" : "PARTIAL";
}

export function formatBlackoutTime(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

export function formatBlackoutPeriod(period, timezone) {
  const daily = `${formatBlackoutTime(period.startTime)} – ${formatBlackoutTime(period.endTime)}`;
  if (!period.startsAt && !period.endsAt) return `Daily ${daily}`;

  const formatBound = (value, exclusiveEnd = false) => {
    if (!value) return "Open";
    const bound = timezone ? moment(value).tz(timezone) : moment(value);
    if (exclusiveEnd) bound.subtract(1, "millisecond");
    return bound.isValid() ? bound.format("MMM D, YYYY") : "Invalid date";
  };

  const equalBounds = Boolean(
    period.startsAt &&
      period.endsAt &&
      moment(period.startsAt).valueOf() === moment(period.endsAt).valueOf(),
  );
  const start = formatBound(period.startsAt);
  const end = equalBounds ? start : formatBound(period.endsAt, true);
  const dates = start === end ? start : `${start} – ${end}`;

  return `${dates} · daily ${daily}`;
}
